import { inject, Injectable } from '@angular/core';
import { map, Observable, of, tap } from 'rxjs';
import { HttpClientService } from '../../../core/services/http-client.service';
import { DASHBOARD_DATA } from '../../../shared/constants/api-routes.constants';
import { PieChartConfig } from '../../../shared/echarts';
import { MatrixTrackerCardData, MatrixTrackerChartData } from '../models/matrix-tracker-chart';

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CacheEntry<T> {
  readonly data: T;
  readonly ts: number;
}

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  private readonly http = inject(HttpClientService);
  private readonly apiPath = DASHBOARD_DATA;

  private readonly trackerCache = new Map<string, CacheEntry<MatrixTrackerCardData>>();

  getTrackerChartData({
    organizationId,
    daysBack,
  }: {
    organizationId: string;
    daysBack: string;
  }): Observable<MatrixTrackerChartData> {
    return this.http.get<any>(`${this.apiPath.TRACKER_CHART_DATA()}`, {
      params: {
        organizationId,
        daysBack,
      },
    });
  }

  getMatrixTrackerCardData({
    organizationId,
    daysBack,
  }: {
    organizationId: string;
    daysBack: string;
  }): Observable<MatrixTrackerCardData> {
    const cacheKey = `${organizationId}-${daysBack}`;
    const cached = this.trackerCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
      return of(cached.data);
    }

    return this.getTrackerChartData({ organizationId, daysBack }).pipe(
      map((data: MatrixTrackerChartData) => {
        const reviewed = data.reviewedCount;
        const outstanding = data.outstandingCount;
        const sentToInvestigation = data.sendToInvestigationCount;
        const totalVariances = data.totalVarianceCount;

        const chartConfig: PieChartConfig = {
          type: 'pie',
          series: [
            {
              name: 'Variances',
              data: [
                {
                  name: 'Reviewed Variance',
                  value: sentToInvestigation,
                  itemStyle: {
                    color: {
                      type: 'linear',
                      x: 0,
                      y: 0,
                      x2: 0,
                      y2: 1,
                      colorStops: [
                        { offset: 0, color: '#88D3EE' }, // lighter cyan
                        { offset: 1, color: '#2C87BD' }, // #88d3ee
                      ],
                    },
                  },
                },
                {
                  name: 'Open Variance',
                  value: outstanding,
                  itemStyle: {
                    color: {
                      type: 'linear',
                      x: 0,
                      y: 0,
                      x2: 0,
                      y2: 1,
                      colorStops: [
                        { offset: 0, color: '#fbbf24' }, // lighter orange
                        { offset: 1, color: '#f59e0b' }, // #f59e0b
                      ],
                    },
                  },
                },
                {
                  name: 'New Variance',
                  value: reviewed,
                  itemStyle: {
                    color: {
                      type: 'linear',
                      x: 0,
                      y: 0,
                      x2: 0,
                      y2: 1,
                      colorStops: [
                        { offset: 0, color: '#a3e635' }, // lighter lime
                        { offset: 1, color: '#84cc16' }, // #84cc16
                      ],
                    },
                  },
                },
              ],
              radius: ['60%', '95%'],
              label: { show: false },
              emphasis: { scale: true },
            },
          ],
          tooltip: { trigger: 'item' },
        };

        return {
          reviewed,
          outstanding,
          sentToInvestigation,
          totalVariances,
          periodLabel: `Last ${daysBack} Days`,
          chartConfig,
        } satisfies MatrixTrackerCardData;
      }),
      tap((result) => this.trackerCache.set(cacheKey, { data: result, ts: Date.now() }))
    );
  }
}
